import { IonButton, IonLoading, IonToast } from '@ionic/react';
import {
    GoogleMap,
    InfoWindow,
    Polygon,
    useJsApiLoader,
} from '@react-google-maps/api';
import { uniqueId } from 'lodash';
import { useCallback, useEffect, useRef, useState } from 'react';
import './GoogleMap.css'

const googleMapLibraries: any[] = ['places', 'marker', 'geometry'];
const mapKey = process.env.REACT_APP_GOOGLE_API_KEY;
const MAP_ID = process.env.REACT_APP_GOOGLE_MAP_ID || 'DEMO_MAP_ID';
const GOOGLE_SHAPE_OPTIONS = {
    fillColor: "white",
    fillOpacity: 0.5,
    strokeWeight: 0,
    clickable: true,
    zIndex: 1,
};

type Coordinates = [number, number];

type TLocation = {
    lat: number;
    lng: number;
};

type CoordinatesArray = Array<Array<Coordinates>>;

const defaultZoom = 20;
type Props = {
    lat?: number;
    lng?: number;
    zoom?: number;
    plotId?: string;
    onPlotsLocationChange: Function;
    onPlotsGeoBoundaryChange?: Function;
    geoBoundary?: Array<[number, number]>;
    defaultEditable?: boolean;
    hideBoundaryTool?: boolean;
}

const Map: React.FC<Props> = (props) => {

    const { onPlotsLocationChange, onPlotsGeoBoundaryChange, geoBoundary, defaultEditable = false, hideBoundaryTool = false } = props;

    const { isLoaded } = useJsApiLoader({
        id: 'google-map-script',
        googleMapsApiKey: mapKey!,
        libraries: googleMapLibraries,
    });

    const mapRef = useRef<any>(null);
    const advancedMarkerRef = useRef<any>(null);
    const autocompleteContainerRef = useRef<HTMLDivElement>(null);
    const [center, setCenter] = useState({ lat: props.lat || 21.7679, lng: props.lng || 78.8718 });
    const [movedCenter, setMovedCenter] = useState<TLocation | null>(null);
    const [zoom, setZoom] = useState(defaultZoom);
    const [mapLoaded, setMapLoaded] = useState(false);

    // maps
    const selectedShapes: any = useRef();

    const [selectedShape, setSelectedShape] = useState<any>({}); // has every shape drawn
    const [currentShape, setCurrentShape] = useState<any>(); // used for deleting the shape
    const [shapePath, setShapePath] = useState<CoordinatesArray>([]); // [[ [lng0, lat0], [lng1, lat1], [lng2, lat2], ... , [lng0, lat0] ]]

    // custom polygon drawing
    const [isDrawingPolygon, setIsDrawingPolygon] = useState(false);
    const [drawingPoints, setDrawingPoints] = useState<TLocation[]>([]);
    const [hasCompletedPolygon, setHasCompletedPolygon] = useState(false);

    // for info model (selected region/shapes)
    const [showoptionsInfoWindow, setShowoptionsInfoWindow] = useState<string>('');
    const [optionsInfoWindowLocation, setOpionsModalLocation] = useState<TLocation>();
    const [savedBoundaryInfoLocation, setSavedBoundaryInfoLocation] = useState<TLocation | null>(null);

    const [isMarkerCovered, setIsMarkerCovered] = useState<boolean>();

    // markers info window
    const [showMarkersInfoWindow, setShowMarkersInfoWindow] = useState<boolean>(false);
    const [isMarkerMoveable, setIsMarkerMoveable] = useState<boolean>(defaultEditable);

    // toast alerts
    const [onSuccess, setOnSuccess] = useState<string>('');
    const [onError, setOnError] = useState<string>('');

    // loader for location updation
    const [updatingLocation, setUpdatingLocation] = useState<boolean>(false);

    useEffect(() => {
        const lat = (props)?.lat;
        const lng = (props)?.lng;
        const zoom = (props)?.zoom;

        const isLatLngUndefined = lat === undefined || lng === undefined;
        const isLatLngZero = lat === 0 && lng === 0;
        if (!(isLatLngUndefined || isLatLngZero)) {
            setCenter({ lat, lng });
        }

        if (zoom !== undefined) {
            setZoom(zoom);
        }
    }, [props]);

    useEffect(() => {
        selectedShapes.current = selectedShape
        if (!!selectedShapes.current) {
            let insideMarkers: boolean = false;

            // just one shape in this case
            for (const shape in selectedShapes.current) {
                const marker = {
                    location: center
                }
                const currentInsideMarkers: any = isMarkerInsideShape(marker, shape)
                insideMarkers = currentInsideMarkers;
            }
            setIsMarkerCovered(insideMarkers);
        }
    }, [selectedShape]);


    /** 
     * ------------------------
     * Map related functions
     * ------------------------
     * */ 

    const onLoad = (map: any) => {
        mapRef.current = map;
        mapRef.current.setMapTypeId('hybrid');
        setMapLoaded(true);
    };

    const onUnmount = useCallback(() => {
        mapRef.current = null;
    }, []);

    // Keep latest handler references so mount-time listeners never go stale.
    const handleMarkerClickRef = useRef<() => void>(() => {});
    const handleMapClickRef = useRef<(event: any) => void>(() => {});
    const onPlaceChangedRef = useRef<(event: any) => void>(() => {});

    useEffect(() => {
        if (!mapLoaded || !mapRef.current) return;
        const marker = new (window as any).google.maps.marker.AdvancedMarkerElement({
            map: mapRef.current,
            position: movedCenter ?? center,
            gmpDraggable: isMarkerMoveable,
            gmpClickable: true,
        });
        const onGmpClick = () => handleMarkerClickRef.current();
        const onDragEnd = (e: any) => handleMapClickRef.current(e);
        marker.addEventListener('gmp-click', onGmpClick);
        marker.addListener('dragend', onDragEnd);
        advancedMarkerRef.current = marker;
        return () => {
            marker.removeEventListener('gmp-click', onGmpClick);
            marker.map = null;
            advancedMarkerRef.current = null;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mapLoaded]);

    useEffect(() => {
        if (advancedMarkerRef.current) {
            advancedMarkerRef.current.position = movedCenter ?? center;
        }
    }, [movedCenter, center]);

    useEffect(() => {
        if (advancedMarkerRef.current) {
            advancedMarkerRef.current.gmpDraggable = isMarkerMoveable;
        }
    }, [isMarkerMoveable]);

    useEffect(() => {
        if (!mapLoaded || !autocompleteContainerRef.current) return;
        const el: any = new (window as any).google.maps.places.PlaceAutocompleteElement();
        const onSelect = (e: any) => onPlaceChangedRef.current(e);
        el.addEventListener('gmp-select', onSelect);
        autocompleteContainerRef.current.appendChild(el);
        return () => {
            el.removeEventListener('gmp-select', onSelect);
            if (el.parentNode) el.parentNode.removeChild(el);
        };
    }, [mapLoaded]);

    /**
     * @function updates position of moved marker
     * is marker isn't moveable, does nothing
     * */ 
    const handleMapClick = (event: any) => {
        if (isDrawingPolygon) {
            const { latLng } = event;
            setDrawingPoints(prev => [...prev, { lat: latLng.lat(), lng: latLng.lng() }]);
            return;
        }
        if (isMarkerMoveable) {
            const { latLng } = event;
            const clickedLat = Number(latLng.lat()).toFixed(6);
            const clickedLng = Number(latLng.lng()).toFixed(6);
            setMovedCenter({ lat: Number(clickedLat), lng: Number(clickedLng) });
            updateMarkerPosition({ lat: Number(clickedLat), lng: Number(clickedLng) });
        }
    }
    handleMapClickRef.current = handleMapClick;

    /**
     * ------------------------
     * Drawing Manager related functions
     * ------------------------
     * */ 

    const onShapeComplete = (shape: any, shapeObject: any, shapeId: string) => {
        clearSelection();
        deleteAllShapes();
        setIsMarkerMoveable(false);
        setCurrentShape(shapeObject);
        if (shape === 'polygon') {
            onPolygonComplete(shapeObject, shapeId)
            return;
        }
    };

    const onPolygonComplete = (shape: any, shapeId: string) => {
        const updatedShape = { ...selectedShapes.current }

        const shapePath = shape.getPath().getArray().map((path: any) => ([path.lng(), path.lat()]))
        shapePath.push(shapePath[0])
        const arrayShapePaths = [shapePath]

        setShapePath(arrayShapePaths)
        updatedShape[shapeId] = { shape, type: 'polygon' };
        setSelectedShape(updatedShape);
    }

    /**
     * @function checks if marker is inside drawn shape
     * uses google-maps methods to check if marker is inside drawn polygon
     * */ 
    const isMarkerInsideShape = (marker: any, shapeId: string) => {
        if (!selectedShapes.current) {
            return true;
        }

        const entry = selectedShapes.current[shapeId];
        if (!entry?.shape) return true;

        return window.google.maps.geometry.poly.containsLocation(
            marker.location,
            entry.shape
        );
    };

    /**
     * @function clears current shape
     * */ 
    const clearSelection = () => {
        if (currentShape) {
            currentShape.setEditable(false);
            setCurrentShape(null)
        }
    }

    /**
     * @function deletes provided shape
     * @param shapeId to delete
     * */ 
    const startDrawingPolygon = () => {
        deleteAllShapes();
        setIsDrawingPolygon(true);
        setDrawingPoints([]);
        setHasCompletedPolygon(false);
    };

    const cancelDrawingPolygon = () => {
        setIsDrawingPolygon(false);
        setDrawingPoints([]);
        setHasCompletedPolygon(false);
        deleteAllShapes();
    };

    const completePolygon = () => {
        if (drawingPoints.length < 3) return;
        const shapeId = uniqueId('polygon-');
        const polygon = new (window as any).google.maps.Polygon({
            paths: drawingPoints,
            ...GOOGLE_SHAPE_OPTIONS,
            map: mapRef.current,
        });
        polygon.addListener('click', () => {
            setCurrentShape(polygon);
            openOptionsWindow(shapeId);
        });
        onShapeComplete('polygon', polygon, shapeId);
        setIsDrawingPolygon(false);
        setDrawingPoints([]);
        setHasCompletedPolygon(true);
    };

    /**
     * @function deletes all drawn shapes
     * */
    const deleteAllShapes = () => {
        if (selectedShapes.current) {
            for (const shapeId in selectedShapes.current) {
                const entry = selectedShapes.current[shapeId];
                if (entry?.shape) entry.shape.setMap(null);
            }
        }
        setSelectedShape({});
        setCurrentShape(null);
        setShowoptionsInfoWindow('');
    }

    /**
     * ------------------------
     * InfoWindow related functions
     * ------------------------
     * */

    /**
     * @function opens info window for marker
     * */
    const handleMarkerClick = () => {
        if (isDrawingPolygon) return;
        setShowMarkersInfoWindow(true);
    }
    handleMarkerClickRef.current = handleMarkerClick;

    /**
     * @function opens info window for drawn shape
     * @param shapeId
     * calculates position for info window
     *  - iterates over the shape path
     *  - maintains array of latlngs for the path
     *  - avg of latlngs is position of info window
     * */ 
    const openOptionsWindow = (shapeId: string) => {
        const entry = selectedShapes.current?.[shapeId];
        if (!entry?.shape) return;
        const { shape, type } = entry;
        let center: TLocation;
        if (type === 'circle') {
            center = shape.getCenter()
        } else {
            /* getting the center of a polygon by
                    using the getPath method
                    then calculating the average of all the latitudes and longitudes of the polygon.
            */
            const path = shape.getPath();
            const latlng = [];

            for (var i = 0; i < path.length; i++) {
                latlng.push({ lat: path.getAt(i).lat(), lng: path.getAt(i).lng() });
            }

            const avgLat = latlng.reduce((a, b) => a + b.lat, 0) / latlng.length;
            const avgLng = latlng.reduce((a, b) => a + b.lng, 0) / latlng.length;

            center = { lat: avgLat, lng: avgLng };
        }

        setOpionsModalLocation(center);
        setShowoptionsInfoWindow(shapeId)
    }

    const closeOptionsInfoWindow = () => {
        setShowoptionsInfoWindow('');
    }

    const closeMarkersInfoWindow = () => {
        setShowMarkersInfoWindow(false);
    }

    /**
     * ------------------------
     * AutoComplete(Search box) related functions
     * ------------------------
     * */

    const onPlaceChanged = async (event: any) => {
        const prediction = event?.placePrediction;
        if (!prediction) return;
        try {
            const place = prediction.toPlace();
            await place.fetchFields({ fields: ['location', 'viewport'] });
            if (place.viewport && mapRef.current) {
                mapRef.current.fitBounds(place.viewport);
            }
            const lat = place.location?.lat();
            const lng = place.location?.lng();
            if (lat && lng) {
                updateMarkerPosition({ lat: Number(lat), lng: Number(lng) });
            }
        } catch (err) {
            console.error('Place fetch failed', err);
        }
    };
    onPlaceChangedRef.current = onPlaceChanged;

    /**
     * Service functions
     * business logic is provided by the parent component
     * */ 

    /**
     * @function adds plot's geo-boundary
     * */ 
    const addGeoBoundary = async () => {
        let proceed = !!isMarkerCovered;

        if (!isMarkerCovered) {
            proceed = window.confirm('No marker inside drawn region, do you want to continue?');
        }

        setShowoptionsInfoWindow('');

        if (!proceed) {
            deleteAllShapes();
            setHasCompletedPolygon(false);
            return;
        }

        try {
            setUpdatingLocation(true);
            setIsMarkerMoveable(false);

            const payload = {
                geoBoundary: {
                    type: "Polygon",
                    coordinates: shapePath
                }
            }

            await onPlotsGeoBoundaryChange?.(payload); // business logic
            setOnSuccess('Geo-Boundary updated successfully');
            deleteAllShapes();
            setHasCompletedPolygon(false);
        } catch (error) {
            setOnError('Failed to add Geo-Boundary, please try again!!');
        } finally {
            setUpdatingLocation(false);
        }
    }

    /**
     * @function updates plot's location
     * */
    const updateMarkerPosition = async (movedCenter: any) => {
        // if (!isMarkerMoveable || !movedCenter) return;

        try {
            setUpdatingLocation(true);
            const payload = {
                location: movedCenter
            };

            await onPlotsLocationChange(payload); // business logic
            setCenter(movedCenter);
            setOnSuccess('Location updated successfully');
        } catch (error) {
            setOnError('Failed to update location, please try again!!');
        } finally {
            setUpdatingLocation(false);
        }
    }

    const cancelMarkerPositionUpdate = () => {
        setIsMarkerMoveable(false);
        setMovedCenter(null);
    }

    const onSavedBoundaryClick = (event: any) => {
        if (!event?.latLng) return;
        setSavedBoundaryInfoLocation({ lat: event.latLng.lat(), lng: event.latLng.lng() });
    }

    const removeSavedBoundary = async () => {
        setSavedBoundaryInfoLocation(null);
        try {
            setUpdatingLocation(true);
            await onPlotsGeoBoundaryChange?.({
                geoBoundary: { type: 'Polygon', coordinates: [] }
            });
            setOnSuccess('Geo-Boundary removed successfully');
        } catch (error) {
            setOnError('Failed to remove Geo-Boundary, please try again!!');
        } finally {
            setUpdatingLocation(false);
        }
    }

    const moveMarker = () => {
        setIsMarkerMoveable(true);
        closeMarkersInfoWindow();
    }

    return (
        <div style={{ height: '100%', zIndex: 10, position: 'relative', width: '100%' }}>
            {isLoaded ? (
                <GoogleMap
                    mapContainerStyle={{
                        width: '100%',
                        height: '100%',
                        position: 'relative',
                        zIndex: 11
                    }}
                    options={{ mapId: MAP_ID }}
                    onLoad={onLoad}
                    onUnmount={onUnmount}
                    zoom={zoom}
                    center={center}
                    onClick={handleMapClick}
                >
                    {
                        (!!geoBoundary && geoBoundary.length > 2)&& <Polygon
                            path={geoBoundary.map((coordinate: any) => ({ lat: coordinate[1], lng: coordinate[0] }))}
                            options={{
                                strokeColor: 'orange',
                                strokeOpacity: 0.4,
                                strokeWeight: 0.5,
                                fillColor: 'orange',
                                fillOpacity: 0.6,
                                clickable: true,
                            }}
                            onClick={onSavedBoundaryClick}
                        />
                    }

                    {/**
                     * Location Search box
                     */}
                    <div
                        id='google-maps-autocomplete-container'
                        ref={autocompleteContainerRef}
                        style={{ display: isMarkerMoveable ? 'block' : 'none' }}
                    />

                    {isDrawingPolygon && drawingPoints.length >= 2 && (
                        <Polygon
                            path={drawingPoints}
                            options={{
                                strokeColor: '#2196F3',
                                strokeOpacity: 0.9,
                                strokeWeight: 2,
                                fillColor: '#2196F3',
                                fillOpacity: 0.2,
                                clickable: false,
                            }}
                        />
                    )}

                    {isDrawingPolygon && (
                        <div style={{
                            position: 'absolute',
                            top: 60,
                            left: '50%',
                            transform: 'translateX(-50%)',
                            background: 'rgba(0,0,0,0.75)',
                            color: 'white',
                            padding: '4px 12px',
                            borderRadius: 4,
                            fontSize: 12,
                            zIndex: 100,
                            whiteSpace: 'nowrap',
                            pointerEvents: 'none',
                        }}>
                            Tap to add points ({drawingPoints.length} added) · Tap Complete when done
                        </div>
                    )}

                    {
                        (!!showoptionsInfoWindow && showoptionsInfoWindow.length > 0 && !isMarkerMoveable) && <InfoWindow
                            position={optionsInfoWindowLocation}
                            onCloseClick={closeOptionsInfoWindow}
                        >
                            <>
                                <IonButton color='primary' expand='block' onClick={addGeoBoundary}> Add Geo Boundary </IonButton>
                                <IonButton color='danger' expand='block' onClick={deleteAllShapes}> Delete Shape </IonButton>
                            </>
                        </InfoWindow>
                    }

                    {
                        savedBoundaryInfoLocation && !isMarkerMoveable && <InfoWindow
                            position={savedBoundaryInfoLocation}
                            onCloseClick={() => setSavedBoundaryInfoLocation(null)}
                        >
                            <IonButton color='danger' expand='block' onClick={removeSavedBoundary}> Delete Geo Boundary </IonButton>
                        </InfoWindow>
                    }

                    {
                        (!!showMarkersInfoWindow && !isMarkerMoveable) && <InfoWindow position={movedCenter ? movedCenter : center} options={{
                            pixelOffset: new window.google.maps.Size(0, -20),
                        }} onCloseClick={closeMarkersInfoWindow}>
                            <>
                                <IonButton color='primary' onClick={moveMarker}> Move Marker </IonButton>
                            </>
                        </InfoWindow>
                    }

                    <div style={{ position: 'absolute', bottom: 5, zIndex: 100, display: 'flex', gap: '8px' }}>
                        {isDrawingPolygon ? (
                            <>
                                {drawingPoints.length >= 3 && (
                                    <IonButton size='small' color='success' onClick={completePolygon}>Complete</IonButton>
                                )}
                                <IonButton size='small' color='danger' onClick={cancelDrawingPolygon}>Cancel</IonButton>
                            </>
                        ) : hasCompletedPolygon ? (
                            <>
                                <IonButton size='small' color='danger' onClick={cancelDrawingPolygon}>Cancel</IonButton>
                                <IonButton size='small' color='primary' onClick={addGeoBoundary}>Save Boundary</IonButton>
                            </>
                        ) : (
                            <>
                                {isMarkerMoveable && (
                                    <>
                                        <IonButton size='small' color='danger' onClick={cancelMarkerPositionUpdate}>Cancel</IonButton>
                                        <IonButton size='small' color='primary' onClick={() => movedCenter && updateMarkerPosition(movedCenter)}>Update Location</IonButton>
                                    </>
                                )}
                                {!hideBoundaryTool && (
                                    <IonButton size='small' color='secondary' onClick={startDrawingPolygon}>Draw Boundary</IonButton>
                                )}
                            </>
                        )}
                    </div>
                </GoogleMap>
            ) : (
                <div>loading...</div>
            )}

            <IonLoading isOpen={updatingLocation} />

            <IonToast
                isOpen={!!onSuccess}
                onDidDismiss={() => setOnSuccess('')}
                message={onSuccess}
                duration={2000}
                color="success"
            />

            <IonToast
                isOpen={!!onError}
                onDidDismiss={() => setOnError('')}
                message={onError}
                duration={2000}
                color="danger"
            />
        </div>
        
    );
};

export default Map;